import React from 'react';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { Button, Input, Typography } from '@deeptrue-frontend/ui';
import {
  Formik,
  Form,
  Field as FormikField,
  FormikProps,
  FormikHelpers,
} from 'formik';
import * as Yup from 'yup';

import countries from 'consts/countries';
import Fields from 'components/Fields';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import { useFormatMessage, useCurrency } from 'components/IntlProvider';
import { ReactComponent as Lock } from '../../../assets/icons/lock.svg';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  padding: 1rem 1.5rem 1rem 0;
  background-color: #ffffff;
  border-top: 1px solid #e3e5e8;
  margin-right: -1.5rem;
  margin-left: -1.5rem;
`;

const StyledForm = styled(Form)`
  margin-top: 3rem;
`;
const StyledLimitsContainer = styled.div`
  width: 12rem;
  margin-right: 4.5rem;
  display: flex;
  flex-direction: column;
`;
const StyledAmountInputWrapper = styled(Grid)`
  position: relative;
  display: flex;
  margin-bottom: 1.5rem !important;
`;

const InputWrapper = styled.div`
  width: 100%;
  label {
    p {
      font-size: 0.875rem;
      font-weight: 600;
      color: #253858;
    }
  }
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 0.625rem;
  }
`;

const StyledInvoiceData = styled.div`
  width: 100%;

  p {
    font-size: 1rem;
    font-weight: 500;
    color: #13244c;
  }
`;

const StyledLabel = styled.label<{ checked: boolean; error: boolean }>`
  border: ${({ checked }) =>
    checked ? '1px solid #1656E9' : '1px solid #c7cad1'};
  border-radius: 4px;
  padding: 0.75rem;
  width: 48%;
  background-color: ${({ checked }) => (checked ? '#E8EFFC' : '#FFFFFF')};
  input {
    margin-right: 1rem;
  }
  cursor: pointer;
  border: ${({ error }) => (error ? '1px solid #DC3545' : '')};
`;

const RadioGroupContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.275rem;
`;

const NameWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  label {
    width: 48%;
    p {
      font-size: 1rem;
      font-weight: 500 !important;
      color: #13244c;
    }
  }
`;

const StyledLabelText = styled.div`
  p {
    font-size: 1rem;
    font-weight: 500;
    color: #13244c;
  }
`;

const ThinInputWrapper = styled.div`
  width: 40%;
`;

const StyledSelect = styled(Fields.ReactSelect)`
  margin-top: -0.5rem;
`;

const FooterTypography = styled(Typography)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 0.75rem;
  }
`;

const ErrorMessage = styled(Typography)<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  font-weight: 400 !important;
  color: #dc3545 !important;
  margin-top: 0.75rem;
`;

const StyledAddressInfoBlock = styled.div<{ isActive?: boolean }>`
  border: 1px solid ${({ isActive }) => (isActive ? '#1F5DEA' : '#e2e2e2')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px;
  min-width: 280px;
  border-radius: 4px;
  margin-right: 1rem;
  margin-top: 1rem;
  position: relative;

  svg {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.punch};
  }
`;

export type SubmitHandler = (
  values: any,
  formHelpers: FormikHelpers<any>
) => void;

export interface FormValues {
  quantity: string;
  type?: string;
  address: string;
  postal_code: string;
  country: string;
  contact_phone: string;
  company_name?: string;
  additional_address?: string;
  city: string;
  contact_email: string;
  company_number?: string;
  first_name?: string;
  last_name?: string;
}

interface PaymentInfoProps {
  onSubmit: SubmitHandler;
  currentBalance: number;
  initialValues: FormValues;
  onCancel?: () => void;
  addressInfoRes?: any;
  removeAddress?: any;
  onAddressChosen?: any;
}

const CreditCardInfo = ({
  currentBalance,
  initialValues,
  onSubmit,
  onCancel,
  addressInfoRes,
  removeAddress,
  onAddressChosen,
}: PaymentInfoProps) => {
  const t = useFormatMessage();
  const formatCurrency = useCurrency();
  const minimumCredits = initialValues.quantity;

  const getValidationSchema = (t: FormatMessage) =>
    Yup.object().shape<FormValues>({
      quantity: Yup.string()
        .test(
          'credits quantity',
          t(
            'errors.fields.credits',
            `You need at least {minimumCredits} Credits`,
            { minimumCredits }
          ),
          (value) => {
            return parseInt(value) >= parseInt(initialValues.quantity);
          }
        )
        .required(t('errors.fields.required', 'Field is required')),
      type: Yup.string().required(
        t('errors.fields.required', 'Field is required')
      ),
      first_name: Yup.string().when('type', {
        is: 'private',
        then: Yup.string().required(
          t('errors.fields.required', 'Field is required')
        ),
      }),
      last_name: Yup.string().when('type', {
        is: 'private',
        then: Yup.string().required(
          t('errors.fields.required', 'Field is required')
        ),
      }),
      company_name: Yup.string().when('type', {
        is: 'company',
        then: Yup.string().required(
          t('errors.fields.required', 'Field is required')
        ),
      }),
      address: Yup.string().required(
        t('errors.fields.required', 'Field is required')
      ),

      postal_code: Yup.string().required(
        t('errors.fields.required', 'Field is required')
      ),
      country: Yup.string().required(
        t('errors.fields.required', 'Field is required')
      ),
      city: Yup.string().required(
        t('errors.fields.required', 'Field is required')
      ),
      contact_email: Yup.string()
        .email(t('errors.fields.email.format', 'Invalid email format'))
        .required(t('errors.fields.required', 'Field is required')),
      contact_phone: Yup.string().matches(
        /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g,
        t('errors.fields.phone', 'Phone number is not valid')
      ),
    });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      noValidate
      enableReinitialize
      validationSchema={getValidationSchema(t)}
    >
      {(props: FormikProps<any>) => {
        return (
          <StyledForm
            onSubmit={!props.isSubmitting ? props.handleSubmit : undefined}
          >
            <StyledAmountInputWrapper item xs={12} sm={12}>
              <StyledLimitsContainer>
                <Typography
                  fontSize={'1rem'}
                  fontWeight={500}
                  color={'#253858'}
                >
                  {t('pages.payment.balance', 'Current Balance')}
                </Typography>
                <Typography
                  fontSize={'1.25rem'}
                  color={'#253858'}
                  fontWeight={500}
                  mt={'0.5rem'}
                >
                  {formatCurrency({
                    value: currentBalance <= 0 ? 0 : currentBalance,
                  })}
                </Typography>
                <Typography
                  fontSize={'1rem'}
                  fontWeight={400}
                  color={'#253858'}
                >
                  {'('}
                  {currentBalance} {t('payments.table.credits', 'Credits')}
                  {')'}
                </Typography>
              </StyledLimitsContainer>
              <InputWrapper>
                <FormikField
                  id="quantity"
                  name="quantity"
                  as={Input}
                  hasError={props.touched.quantity && props.errors.quantity}
                  helperText={props.touched.quantity && props.errors.quantity}
                  label={t('forms.credit_card_info.amount', 'New Credits')}
                />
                <StyledTypography
                  fontSize={'0.875rem'}
                  fontWeight={400}
                  color={'#253858'}
                  mt={'-0.5rem'}
                >
                  Value equal to{' '}
                  {formatCurrency({
                    value:
                      props.values.quantity <= 0 ? 0 : props.values.quantity,
                  })}
                </StyledTypography>
              </InputWrapper>
            </StyledAmountInputWrapper>
            <StyledInvoiceData>
              <div style={{ borderBottom: '1px solid #E3E5E8' }} />
              <div>
                <RadioGroupContainer role="group">
                  <Typography
                    fontSize={'1rem'}
                    fontWeight={500}
                    color={'#253858'}
                    mt={'1rem'}
                    mb={'1rem'}
                  >
                    {t('payment.modal.invoice.data', 'Invoice data')}
                  </Typography>
                  <FlexContainer>
                    <StyledLabel
                      checked={props.values.type === 'company'}
                      error={!!props.errors.type}
                    >
                      <FormikField type="radio" name="type" value="company" />
                      {t('forms.company', 'Company')}
                    </StyledLabel>
                    <StyledLabel
                      checked={props.values.type === 'private'}
                      error={!!props.errors.type}
                    >
                      <FormikField type="radio" name="type" value="private" />
                      {t('forms.private', 'Private person')}
                    </StyledLabel>
                  </FlexContainer>
                  <ErrorMessage isVisible={!!props.errors.type}>
                    {t('errors.fields.required', 'Field is required')}
                  </ErrorMessage>
                </RadioGroupContainer>
              </div>
              {props.values.type === 'company' && (
                <StyledLabelText>
                  <FormikField
                    tabIndex={6}
                    name="company_name"
                    as={Input}
                    label={t(
                      'forms.credit_card_info.company_name',
                      'Company name'
                    )}
                    hasError={
                      props.touched.company_name && props.errors.company_name
                    }
                    helperText={
                      props.touched.company_name && props.errors.company_name
                    }
                  />
                </StyledLabelText>
              )}
              {props.values.type === 'private' && (
                <NameWrapper>
                  <FormikField
                    tabIndex={1}
                    name="first_name"
                    as={Input}
                    label={t('forms.credit_card_info.first_name', 'First name')}
                    hasError={
                      props.touched.first_name && props.errors.first_name
                    }
                    helperText={
                      props.touched.first_name && props.errors.first_name
                    }
                  />

                  <FormikField
                    tabIndex={2}
                    name="last_name"
                    as={Input}
                    label={t('forms.credit_card_info.last_name', 'Last name')}
                    hasError={props.touched.last_name && props.errors.last_name}
                    helperText={
                      props.touched.last_name && props.errors.last_name
                    }
                  />
                </NameWrapper>
              )}
              <FormikField
                tabIndex={6}
                name="contact_email"
                as={Input}
                label={t('forms.credit_card_info.email', 'Email')}
                hasError={
                  props.touched.contact_email && props.errors.contact_email
                }
                helperText={
                  props.touched.contact_email && props.errors.contact_email
                }
              />
              {props.values.type === 'company' && (
                <FormikField
                  tabIndex={6}
                  name="company_number"
                  as={Input}
                  label={t(
                    'forms.credit_card_info.company_number',
                    'Unique company number (optional)'
                  )}
                />
              )}
            </StyledInvoiceData>
            {addressInfoRes.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  marginTop: '2rem',
                  marginBottom: '2rem',
                }}
              >
                {addressInfoRes.map((addressInfoRes: any) => {
                  return (
                    <StyledAddressInfoBlock key={addressInfoRes.id}>
                      <div>
                        <Typography>{addressInfoRes.address}</Typography>
                        <Typography>{addressInfoRes.postal_code}</Typography>
                        <Typography>{addressInfoRes.city}</Typography>
                      </div>
                      <Button
                        onClick={() => {
                          onAddressChosen(addressInfoRes);
                          props.setFieldValue(
                            'address',
                            addressInfoRes.address
                          );
                          props.setFieldValue(
                            'postal_code',
                            addressInfoRes.postal_code
                          );
                          addressInfoRes.additional_address &&
                            props.setFieldValue(
                              'additional_address',
                              addressInfoRes.additional_address
                            );
                          props.setFieldValue('city', addressInfoRes.city);
                        }}
                        variant={'secondary'}
                        type="button"
                      >
                        {t('pages.payment.use', 'Apply')}
                      </Button>
                      <DeleteForeverIcon
                        fontSize="small"
                        onClick={() => removeAddress(addressInfoRes.id)}
                      />
                    </StyledAddressInfoBlock>
                  );
                })}
              </div>
            )}

            <StyledInvoiceData>
              <FlexContainer>
                <FormikField
                  tabIndex={1}
                  name="address"
                  as={Input}
                  label={t('forms.credit_card_info.address', 'Address')}
                  hasError={props.touched.address && props.errors.address}
                  helperText={props.touched.address && props.errors.address}
                />
                <ThinInputWrapper>
                  <FormikField
                    class={'formik'}
                    tabIndex={2}
                    name="additional_address"
                    as={Input}
                    label={t(
                      'forms.credit_card_info.additional_address',
                      'Apt/Suite (optional)'
                    )}
                  />
                </ThinInputWrapper>
              </FlexContainer>
              <FlexContainer>
                <ThinInputWrapper>
                  <FormikField
                    tabIndex={3}
                    name="postal_code"
                    as={Input}
                    label={t(
                      'forms.credit_card_info.postal_code',
                      'Postal Code'
                    )}
                    hasError={
                      props.touched.postal_code && props.errors.postal_code
                    }
                    helperText={
                      props.touched.postal_code && props.errors.postal_code
                    }
                  />
                </ThinInputWrapper>
                <FormikField
                  tabIndex={4}
                  name="city"
                  as={Input}
                  label={t('forms.credit_card_info.city', 'City')}
                  hasError={props.touched.city && props.errors.city}
                  helperText={props.touched.city && props.errors.city}
                />
              </FlexContainer>
              <FlexContainer>
                <ThinInputWrapper>
                  <FormikField
                    tabIndex={5}
                    name="country"
                    component={StyledSelect}
                    label={t('forms.credit_card_info.country', 'Country')}
                    isClearable={false}
                    hasError={props.touched.country && props.errors.country}
                    helperText={props.touched.country && props.errors.country}
                    options={Object.keys(countries).map((code: any) => ({
                      value: code,
                      label: countries[code],
                    }))}
                  />
                </ThinInputWrapper>
                <FormikField
                  autoComplete="off"
                  tabIndex={7}
                  name="contact_phone"
                  as={Input}
                  placeholder="+00 1234-1234"
                  label={t('forms.credit_card_info.phone', 'Contact Phone')}
                  hasError={
                    props.touched.contact_phone && props.errors.contact_phone
                  }
                  helperText={
                    props.touched.contact_phone && props.errors.contact_phone
                  }
                />
              </FlexContainer>
            </StyledInvoiceData>
            <FooterTypography
              color={'#5C6370'}
              fontSize={'0.75rem'}
              fontWeight={400}
              mb={'1rem'}
            >
              <Lock />
              {t(
                'forms.profile.footnote',
                'We use industry-standard encryption to protect the confidentiality of your personal information. This transaction will be managed by Stripe.'
              )}
            </FooterTypography>
            <StyledButtonContainer>
              <Button
                width="fit-content"
                height="3rem"
                type="submit"
                variant="secondary"
                marginRight="0.5rem"
                onClick={onCancel}
              >
                {t('dashboard.template.btn.cancel', 'Cancel')}
              </Button>
              <Button
                width="fit-content"
                height="3rem"
                type="submit"
                variant="primary"
                disabled={props.isSubmitting || !props.isValid}
                isLoading={props.isSubmitting}
              >
                {t('forms.profile.buy', 'Procced with Top-Up')}
              </Button>
            </StyledButtonContainer>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export default CreditCardInfo;
